import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import supabase from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { sendSMSViaTwilio, updateSMSStatus } from '../../utils/smsWebhookHandler';
import { fetchRecipientData, extractPhoneNumbers } from '../../utils/dataExport';
import { shiftOptions } from '../../constants/shiftData';



const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
  padding: theme.spacing(3),
  },
  margin: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    margin: theme.spacing(1.5),
  },
  [theme.breakpoints.up('md')]: {
  margin: theme.spacing(2),
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: { xs: '100%', sm: 200 },
  width: { xs: '100%', sm: 'auto' },
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
    },
  },
};

function SMSPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Initialize tab value from URL parameter or default to 0
  const getInitialTabValue = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab === 'history') return 1;
    return 0; // default to 'send'
  };
  
  const [tabValue, setTabValue] = useState(getInitialTabValue);
  const [recipientType, setRecipientType] = useState('dugsi');
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [individualPhoneNumber, setIndividualPhoneNumber] = useState('');
  const [selectedRoles, setSelectedRoles] = useState({
    parents: true,
    teachers: false,
  });
  const [classes, setClasses] = useState([]);
  const [message, setMessage] = useState('');
  const [messageHistory, setMessageHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  useEffect(() => {
    const fetchClasses = async () => {
      const { data, error } = await supabase
        .from('classes')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setClasses(data);
      }
    };

    const fetchCurrentAdmin = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('admins')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();
        
        if (!error && data) {
          setCurrentAdmin(data);
        }
      }
    };

    fetchClasses();
    fetchCurrentAdmin();
  }, [user]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchMessageHistory();
    }
  }, [tabValue]);

  // Handle URL changes (back/forward navigation)
  useEffect(() => {
    const newTabValue = getInitialTabValue();
    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Update URL parameter
    const urlParams = new URLSearchParams(location.search);
    if (newValue === 1) {
      urlParams.set('tab', 'history');
    } else {
      urlParams.delete('tab'); // Default tab, no parameter needed
    }
    
    const newSearch = urlParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newUrl, { replace: true });
  };

  const fetchMessageHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('sms_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100); // Limit to last 100 messages for performance

      if (error) {
        throw error;
      }

      // Transform data to match the expected format
      const transformedHistory = data.map(log => ({
        id: log.id,
        message: log.message,
        recipient_type: log.recipient_type,
        recipient_details: log.recipient_details,
        recipient_count: log.recipient_count,
        sent_at: log.sent_at || log.created_at,
        sent_by: log.sent_by_name,
        status: log.status
      }));

      setMessageHistory(transformedHistory);
    } catch (error) {
      console.error('Error fetching message history:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load message history',
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };










  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate message content
      if (!message.trim()) {
        throw new Error('Please enter a message');
      }

      let phoneNumbers = [];
      let recipientDetails = '';

      // Handle individual phone number case
      if (recipientType === 'individual') {
        if (!individualPhoneNumber.trim()) {
          throw new Error('Please enter a phone number');
        }
        // Remove any non-digit characters and validate it's exactly 10 digits
        const digitsOnly = individualPhoneNumber.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
          throw new Error('Please enter a valid 10-digit US phone number (e.g., 1234567890)');
        }
        // Format as +1XXXXXXXXXX
        const formattedPhone = `+1${digitsOnly}`;
        phoneNumbers = [formattedPhone];
        recipientDetails = formattedPhone;
      } else {
        // Validate that at least one role is selected for non-individual types
        if (!selectedRoles.parents && !selectedRoles.teachers) {
          throw new Error('Please select at least one recipient type (Parents or Teachers)');
        }

        // Validate required fields based on recipient type
        if (recipientType === 'shift' && selectedShifts.length === 0) {
          throw new Error('Please select at least one shift');
        }
        if (recipientType === 'class' && selectedClasses.length === 0) {
          throw new Error('Please select at least one class');
        }

        // Fetch recipient data and extract phone numbers
        const recipientData = await fetchRecipientData(
          recipientType,
          selectedShifts,
          selectedClasses,
          {
            includeParents: selectedRoles.parents,
            includeTeachers: selectedRoles.teachers,
          }
        );
        
        phoneNumbers = extractPhoneNumbers(recipientData);

        if (phoneNumbers.length === 0) {
          throw new Error('No recipients found with valid phone numbers');
        }

        // Create recipient details for display
        if (recipientType === 'dugsi') {
          recipientDetails = 'Entire Dugsi';
        } else if (recipientType === 'shift') {
          recipientDetails = `Shift: ${selectedShifts.join(', ')}`;
        } else if (recipientType === 'class') {
          const selectedClassNames = classes
            .filter(cls => selectedClasses.includes(cls.id))
            .map(cls => cls.name)
            .join(', ');
          recipientDetails = `Class: ${selectedClassNames}`;
        }
      }

      // Log message to database
      const { data: logData, error: logError } = await supabase
        .from('sms_logs')
        .insert({
          message: message.trim(),
          recipient_type: recipientType,
          recipient_details: recipientDetails,
          selected_shifts: recipientType === 'shift' ? selectedShifts : null,
          selected_classes: recipientType === 'class' ? selectedClasses : null,
          include_parents: recipientType === 'individual' ? false : selectedRoles.parents,
          include_teachers: recipientType === 'individual' ? false : selectedRoles.teachers,
          phone_numbers: phoneNumbers,
          recipient_count: phoneNumbers.length,
          sent_by_admin_id: user.id,
          sent_by_name: currentAdmin ? `${currentAdmin.first_name} ${currentAdmin.last_name}` : 'Unknown Admin',
          status: 'pending', // Start with pending status
          provider_name: 'internal'
        })
        .select()
        .single();

      if (logError) {
        console.error('Error logging SMS:', logError);
        throw new Error('Failed to log message. Please try again.');
      }

      console.log('✅ SMS logged successfully:', logData);

      // Update status to queued before sending
      await updateSMSStatus(logData.id, 'queued');

      // Attempt to send SMS via provider
      try {
        const sendResult = await sendSMSViaTwilio(phoneNumbers, message.trim(), logData.id);
        
        if (sendResult.success) {
          await updateSMSStatus(logData.id, 'sent', {
            sent_at: new Date().toISOString(),
            provider_name: sendResult.provider || 'twilio'
          });
          
          // Handle partial success/failure
          const totalCount = sendResult.totalCount || phoneNumbers.length;
          const successCount = sendResult.successCount || totalCount;
          const failureCount = sendResult.failureCount || 0;
          
          if (failureCount > 0) {
            setSnackbar({
              open: true,
              message: `Message sent to ${successCount} of ${totalCount} recipient(s). ${failureCount} failed.`,
              severity: 'warning'
            });
          } else {
            setSnackbar({
              open: true,
              message: `Message sent to ${successCount} recipient(s) successfully!`,
              severity: 'success'
            });
          }
        } else {
          await updateSMSStatus(logData.id, 'failed', {
            error_message: sendResult.error || 'Failed to send SMS'
          });
          
          setSnackbar({
            open: true,
            message: `Failed to send message: ${sendResult.error || 'Unknown error'}`,
            severity: 'error'
          });
        }
      } catch (sendError) {
        console.error('Error sending SMS:', sendError);
        await updateSMSStatus(logData.id, 'failed', {
          error_message: sendError.message
        });
        
        setSnackbar({
          open: true,
          message: `Failed to send message: ${sendError.message}`,
          severity: 'error'
        });
      }
      
      // Clear form
      setMessage('');
      if (recipientType === 'shift') {
        setSelectedShifts([]);
      } else if (recipientType === 'class') {
        setSelectedClasses([]);
      } else if (recipientType === 'individual') {
        setIndividualPhoneNumber('');
      }
      
      // Refresh message history if on history tab
      if (tabValue === 1) {
        fetchMessageHistory();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send message',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRoleChange = (role) => {
    setSelectedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  const handleHistoryPageChange = (event, newPage) => {
    setHistoryPage(newPage);
  };

  const handleHistoryRowsPerPageChange = (event) => {
    setHistoryRowsPerPage(parseInt(event.target.value, 10));
    setHistoryPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumberDisplay = (digits) => {
    if (!digits) return '';
    // Format as (123) 456-7890
    if (digits.length <= 3) {
      return `(${digits}`;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'pending': return 'warning';
      case 'sent': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <StyledPaper>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          mb: { xs: 2, sm: 3 },
        }}
      >
        SMS Management
      </Typography>
      
      {/* Tabs */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 1.5, sm: 2 },
              minWidth: { xs: 'auto', sm: 0 },
              px: { xs: 2, sm: 3 },
            },
          }}
        >
          <Tab
            icon={<SendIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
            label="Send Message"
            iconPosition="start"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          />
          <Tab
            icon={<HistoryIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
            label="Message History"
            iconPosition="start"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <form onSubmit={handleSubmit}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <StyledFormControl fullWidth>
              <InputLabel>Send To</InputLabel>
              <Select
                value={recipientType}
                onChange={(e) => {
                  setRecipientType(e.target.value);
                  // Clear individual phone when switching away
                  if (e.target.value !== 'individual') {
                    setIndividualPhoneNumber('');
                  }
                }}
                label="Send To"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="dugsi">Entire Dugsi</MenuItem>
                <MenuItem value="shift">Specific Shift</MenuItem>
                <MenuItem value="class">Specific Class</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>

          {recipientType === 'shift' && (
            <Grid item xs={12}>
              <StyledFormControl fullWidth>
                <InputLabel>Select Shifts</InputLabel>
                <Select
                  multiple
                  value={selectedShifts}
                  onChange={(e) => setSelectedShifts(e.target.value)}
                  label="Select Shifts"
                  required
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={MenuProps}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                >
                  {shiftOptions.map((shift) => (
                    <MenuItem key={shift} value={shift}>
                      <ListItemIcon>
                        <Checkbox checked={selectedShifts.indexOf(shift) > -1} />
                      </ListItemIcon>
                      {shift}
                    </MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            </Grid>
          )}

          {recipientType === 'class' && (
            <Grid item xs={12}>
              <StyledFormControl fullWidth>
                <InputLabel>Select Classes</InputLabel>
                <Select
                  multiple
                  value={selectedClasses}
                  onChange={(e) => setSelectedClasses(e.target.value)}
                  label="Select Classes"
                  required
                  renderValue={(selected) => {
                    if (selected.length === 0) return '';
                    return `${selected.length} class${selected.length > 1 ? 'es' : ''} selected`;
                  }}
                  MenuProps={MenuProps}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      <ListItemIcon>
                        <Checkbox checked={selectedClasses.indexOf(cls.id) > -1} />
                      </ListItemIcon>
                      {cls.name}
                    </MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            </Grid>
          )}

          {recipientType === 'individual' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formatPhoneNumberDisplay(individualPhoneNumber)}
                onChange={(e) => {
                  // Only allow digits, remove any non-digit characters
                  const digitsOnly = e.target.value.replace(/\D/g, '');
                  // Limit to 10 digits
                  const limitedDigits = digitsOnly.slice(0, 10);
                  setIndividualPhoneNumber(limitedDigits);
                }}
                placeholder="(123) 456-7890"
                required
                helperText="Enter 10-digit US phone number"
                inputProps={{
                  maxLength: 14, // (123) 456-7890 = 14 characters
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
          )}

          {recipientType !== 'individual' && (
            <Grid item xs={12}>
              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Recipients
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0.5, sm: 2 },
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRoles.parents}
                      onChange={() => handleRoleChange('parents')}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1.125rem', sm: '1.5rem' },
                          },
                        }}
                    />
                  }
                  label="Parents"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRoles.teachers}
                      onChange={() => handleRoleChange('teachers')}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1.125rem', sm: '1.5rem' },
                          },
                        }}
                    />
                  }
                  label="Teachers"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                />
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={4}
              required
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={recipientType !== 'individual' && !selectedRoles.parents && !selectedRoles.teachers}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1.25, sm: 1.5 },
              }}
            >
              Send Message
            </Button>
          </Grid>
        </Grid>
        </form>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Message History
            </Typography>
            <Button 
              variant="outlined" 
              onClick={fetchMessageHistory}
              disabled={historyLoading}
              fullWidth={isMobile}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              {historyLoading ? <CircularProgress size={isMobile ? 18 : 20} /> : 'Refresh'}
            </Button>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 3, sm: 4 } }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer 
                component={Paper} 
                variant="outlined"
                sx={{
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                        display: { xs: 'none', sm: 'table-cell' },
                      }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        Message
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                        display: { xs: 'none', md: 'table-cell' },
                      }}>
                        Recipients
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        Count
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                        display: { xs: 'none', lg: 'table-cell' },
                      }}>
                        Sent By
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messageHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 3 : 6} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          >
                            No message history found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      messageHistory
                      .slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage)
                      .map((msg) => (
                        <TableRow key={msg.id}>
                            <TableCell sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              display: { xs: 'none', sm: 'table-cell' },
                            }}>
                              {formatDate(msg.sent_at)}
                            </TableCell>
                            <TableCell sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              maxWidth: { xs: 200, sm: 300 },
                            }}>
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  noWrap={!isMobile}
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                              {msg.message}
                            </Typography>
                                {/* Show date on mobile */}
                                {isMobile && (
                                  <Typography 
                                    variant="caption" 
                                    color="textSecondary"
                                    sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                                  >
                                    {formatDate(msg.sent_at)}
                                  </Typography>
                                )}
                                {/* Show recipients on mobile */}
                                {isMobile && (
                                  <Typography 
                                    variant="caption" 
                                    color="textSecondary"
                                    sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5, textTransform: 'capitalize' }}
                                  >
                                    {msg.recipient_details}
                                  </Typography>
                                )}
                                {/* Show sent by on mobile */}
                                {isMobile && (
                                  <Typography 
                                    variant="caption" 
                                    color="textSecondary"
                                    sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                                  >
                                    By: {msg.sent_by}
                                  </Typography>
                                )}
                              </Box>
                          </TableCell>
                            <TableCell sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              display: { xs: 'none', md: 'table-cell' },
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textTransform: 'capitalize',
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                              {msg.recipient_details}
                            </Typography>
                          </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {msg.recipient_count}
                            </TableCell>
                            <TableCell sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              display: { xs: 'none', lg: 'table-cell' },
                            }}>
                              {msg.sent_by}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Chip 
                              label={msg.status} 
                              color={getStatusColor(msg.status)}
                              size="small"
                                sx={{ 
                                  textTransform: 'capitalize',
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  height: { xs: '20px', sm: '24px' },
                                }}
                            />
                          </TableCell>
                        </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={messageHistory.length}
                page={historyPage}
                onPageChange={handleHistoryPageChange}
                rowsPerPage={historyRowsPerPage}
                onRowsPerPageChange={handleHistoryRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 0 },
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </>
          )}
        </Box>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </StyledPaper>
  );
}

export default SMSPage; 